import {
  ADHA_JOURNEY_LOG_PREFIX,
  readConvertedLeadFlag,
} from '@/core/constants/adhaJourney';
import type { AvailableUnit } from '@/core/api/models/properties';
import { unitsService } from '@/core/api/services/unitsService';
import { useTranslation } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useAppStore, type User } from '@/core/stores/appStore';
import { mapCatalogueProfileToLoanDetails } from '@/modules/auth/services/adhaCatalogueProfile';
import {
  beginFreshNativeAdhaLogin,
  endNativeAdhaLogin,
  installAdhaUaePassDebugListeners,
} from '@/modules/auth/services/adhaUaePassNative';
import { adhaUaePassService } from '@/modules/auth/services/adhaUaePassService';
import { authService } from '@/modules/auth/services/authService';
import { hydrateNormalCustomerFromProfile } from '@/modules/auth/services/hydrateNormalCustomerFromProfile';
import {
  mapProfileDetailsToUserPatch,
  profileService,
} from '@/modules/auth/services/profileService';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { resolveDynatraceUserIdentity, setDynatraceUserEmail } from '@/core/services/dynatraceService';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { loadAdhaTranslationsOnly } from '@/core/services/translationService';
import {
  clearAdhaDeepLink,
  consumeAdhaPostLoginDeepLink,
} from '@/modules/properties-web/utils/adhaDeepLinkRedirect';
import { useRouter, type Href } from 'expo-router';
import { useCallback, useState } from 'react';
import { Platform } from 'react-native';

function mapDetailsToUser(details: {
  full_name: string;
  email: string;
  phone_number: string;
  emirates_id: string;
}): User {
  return {
    id: `adha:${details.emirates_id || details.email || 'user'}`,
    name: details.full_name,
    full_name: details.full_name,
    primary_email: details.email,
    primary_phone: details.phone_number,
    emairates_id: details.emirates_id,
  };
}

async function fetchNativeAvailableUnits(projectId: string): Promise<AvailableUnit[]> {
  try {
    const listResponse = await unitsService.getAvailableUnits({
      projectId,
      page: 0,
      limit: 20,
      maxPrice: 5_200_000,
    });
    return listResponse.units || [];
  } catch (listError) {
    logger.warn(`${ADHA_JOURNEY_LOG_PREFIX}:unit list failed — continuing with profile only`, {
      error: listError,
      projectId,
    });
    return [];
  }
}

async function establishAdhaCatalogueSession(params: Readonly<{
  jwtToken: string;
  setAdhaSession: ReturnType<typeof useAppStore.getState>['setAdhaSession'];
}>): Promise<void> {
  const { jwtToken, setAdhaSession } = params;

  useAppStore.setState((state) => ({
    auth: { ...state.auth, token: jwtToken },
  }));

  const profileDetails = await profileService.getProfileDetails();
  const profilePatch = mapProfileDetailsToUserPatch(profileDetails);
  const emiratesIdFromDetails = profileDetails.emairates_id?.trim() ?? '';
  if (!emiratesIdFromDetails) {
    throw new Error('ADHA_PROFILE_MISSING_EMIRATES_ID');
  }
  logger.info(`${ADHA_JOURNEY_LOG_PREFIX}:profile-details OK`, {
    emairates_id: emiratesIdFromDetails,
    customerId: profilePatch.customer_id,
    salesAgentId: profilePatch.sales_agent_id,
  });

  const detailsResponse = await unitsService.getUnitDetails();
  const projectDetails = detailsResponse.details;
  const user: User = {
    ...mapDetailsToUser(projectDetails),
    ...profilePatch,
  };
  const loanDetails = mapCatalogueProfileToLoanDetails(
    projectDetails,
    profileDetails.loan_Amount,
  );
  const projectId = projectDetails.project_id;

  if (!projectId) {
    throw new Error('ADHA_PROFILE_MISSING_PROJECT_ID');
  }

  const units =
    Platform.OS !== 'web' ? await fetchNativeAvailableUnits(projectId) : [];

  const dynatraceUserId = resolveDynatraceUserIdentity(user);
  if (dynatraceUserId) {
    await setDynatraceUserEmail(dynatraceUserId);
  }

  setAdhaSession(user, {
    availableUnits: units,
    loanDetails,
    projectDetails,
    token: jwtToken,
  });

  logger.info(`${ADHA_JOURNEY_LOG_PREFIX}:session ready`, {
    projectId,
    unitCount: units.length,
    sanctionedLoanAmount: loanDetails.sanctionedLoanAmount,
  });
}

/**
 * ADHA entry (UAE Pass button).
 *
 * Web: same-tab redirect to UAE Pass OAuth → `/uae-pass` callback → welcome → API login.
 * Native: push `/uae-pass` and complete login from the WebView with the OAuth code.
 *
 * When `/v1/uae-pass/login` returns `convertedLead: true`, user gets a normal
 * dashboard session instead of the ADHA catalogue journey.
 */
export function useAdhaLogin() {
  const router = useRouter();
  const { t, currentLanguage } = useTranslation(true);
  const setAdhaSession = useAppStore((s) => s.setAdhaSession);
  const clearAuth = useAppStore((s) => s.clearAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finishNativeAndroidLogin = useCallback(() => {
    if (Platform.OS === 'android') {
      endNativeAdhaLogin();
    }
  }, []);

  const navigateToAuthenticatedHome = useCallback(() => {
    if (Platform.OS === 'web') {
      clearAdhaDeepLink();
    }
    router.replace('/(auth)/(home)');
  }, [router]);

  const navigateAfterAdhaSession = useCallback(() => {
    const deepLink = Platform.OS === 'web' ? consumeAdhaPostLoginDeepLink() : null;
    if (deepLink) {
      logger.info(`${ADHA_JOURNEY_LOG_PREFIX}:redirecting to pending deep link`, { deepLink });
      router.replace(deepLink as Href);
      return;
    }
    router.replace('/(auth)/(home)');
  }, [router]);

  const completeAdhaLoginWithCode = useCallback(
    async (oauthCode: string) => {
      setIsLoading(true);
      setError(null);

      try {
        logger.info(`${ADHA_JOURNEY_LOG_PREFIX}:login start`, { source: 'oauth-code' });

        // Android Expo remounts /uae-pass while this POST is in flight. Ending
        // the native flow here makes that remount treat the session as idle and
        // replace /login before the API finishes. iOS does not remount.
        if (Platform.OS !== 'android') {
          endNativeAdhaLogin();
        }
        const adhaLoginResponse = await authService.loginWithAdhaUaePassCode(oauthCode);
        const adhaJwtToken = adhaLoginResponse.data?.jwtToken?.trim() ?? '';
        if (adhaLoginResponse.status === 'error' || !adhaJwtToken) {
          const apiError = new Error(
            adhaLoginResponse.message || 'ADHA_LOGIN_FAILED',
          ) as Error & { response?: unknown };
          apiError.response = adhaLoginResponse;
          throw apiError;
        }

        const convertedLead = readConvertedLeadFlag(adhaLoginResponse);
        if (convertedLead) {
          // Same as email login: profile-details → user + properties on store
          await hydrateNormalCustomerFromProfile(adhaJwtToken, 'adha', convertedLead);
          finishNativeAndroidLogin();
          navigateToAuthenticatedHome();
          trackCatalogEvent({
            screen: ALMETLAA_SCREEN.AUTH,
            actionName: ALMETLAA_ACTION.AUTH.ADHA_SESSION_SUCCESS,
            eventType: 'action',
          });
          return;
        }

        await establishAdhaCatalogueSession({
          jwtToken: adhaJwtToken,
          setAdhaSession,
        });
        finishNativeAndroidLogin();
        navigateAfterAdhaSession();
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.AUTH,
          actionName: ALMETLAA_ACTION.AUTH.ADHA_SESSION_SUCCESS,
          eventType: 'action',
        });
      } catch (err: unknown) {
        await clearAuth();
        logger.error(`${ADHA_JOURNEY_LOG_PREFIX}:failed`, err);
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.AUTH,
          actionName: ALMETLAA_ACTION.AUTH.ADHA_SESSION_ERROR,
          eventType: 'error',
          properties: {
            p_failedMessage: err instanceof Error ? err.message : String(err),
          },
        });
        // Callers show MessageOkDialog via resolveAdhaUaePassUserMessage — not LoginDialog inline.
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [
      clearAuth,
      finishNativeAndroidLogin,
      navigateAfterAdhaSession,
      navigateToAuthenticatedHome,
      setAdhaSession,
    ],
  );

  const startAdhaUaePassOAuth = useCallback(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    adhaUaePassService.startWebAuthorization();
    setError(null);
  }, []);

  const loginWithAdha = useCallback(() => {
    if (Platform.OS === 'web') {
      startAdhaUaePassOAuth();
      return;
    }

    setError(null);
    adhaUaePassService.setNativePendingUi(null);
    beginFreshNativeAdhaLogin();
    installAdhaUaePassDebugListeners();
    try {
      router.replace('/uae-pass');
    } catch (err: unknown) {
      logger.error(`${ADHA_JOURNEY_LOG_PREFIX}:native navigate /uae-pass failed`, err);
      void loadAdhaTranslationsOnly(currentLanguage).finally(() => {
        setError(t('auth.adhaUaePass.somethingWentWrong'));
      });
    }
  }, [currentLanguage, router, startAdhaUaePassOAuth, t]);

  const applyInlineError = useCallback((message: string) => {
    setError(message);
  }, []);

  const completeAdhaLoginWithToken = useCallback(
    async (adhaJwtToken: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await establishAdhaCatalogueSession({
          jwtToken: adhaJwtToken,
          setAdhaSession,
        });
        finishNativeAndroidLogin();
        navigateAfterAdhaSession();
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.AUTH,
          actionName: ALMETLAA_ACTION.AUTH.ADHA_SESSION_SUCCESS,
          eventType: 'action',
        });
      } catch (err: unknown) {
        await clearAuth();
        trackCatalogEvent({
          screen: ALMETLAA_SCREEN.AUTH,
          actionName: ALMETLAA_ACTION.AUTH.ADHA_SESSION_ERROR,
          eventType: 'error',
          properties: {
            p_failedMessage: err instanceof Error ? err.message : String(err),
          },
        });
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [clearAuth, finishNativeAndroidLogin, navigateAfterAdhaSession, setAdhaSession],
  );

  return {
    loginWithAdha,
    completeAdhaLoginWithCode,
    completeAdhaLoginWithToken,
    startAdhaUaePassOAuth,
    isLoading,
    error,
    clearError: () => setError(null),
    applyInlineError,
  };
}
